import { FormElement, ElementData, createElement } from "../../records/Element";
import { FormConfig } from "../../records/Form";
import * as ApiResponse from "../api/types";
import { componentMap } from "../../constants";

type ElementOutput = { [k: string]: FormElement };

/**
 * Normalise an array of elements
 * @param context an array of elements to be normalised
 * @param viewId view id
 * @param output optional object containing normalised elements to be mutated
 */
export const normaliseElements = (
	context: ApiResponse.ElementDefinition,
	viewId: string,
	output: ElementOutput = {}
) => {
	switch (context.elem) {
		case "date":
		case "lookup":
		case "radio":
		case "checkbox":
		case "reference":
		case "select":
		case "text":
		case "header":
		case "paragraph":
		case "media":
		case "textarea":
		case "number": {
			output[context.uuid] = createElement({ vid: viewId, ...context });
			break;
		}

		case "panel":
		case "legacyArrayView": {
			output[context.uuid] = createElement({
				...context,
				vid: viewId,
				traversal: context.elements
					? removeUnknownElements(context.elements).map(el => {
							normaliseElements(el, viewId, output);
							return el.uuid;
					  })
					: [],
			});
			break;
		}

		case "multiReferenceView":
		case "nestedView":
		case "rootView": {
			output[context.uuid] = createElement({
				...context,
				vid: context.id,
				traversal: context.elements
					? removeUnknownElements(context.elements).map(el => {
							normaliseElements(el, context.id, output);
							return el.uuid;
					  })
					: [],
			});
			break;
		}
	}

	return output;
};

/**
 * Filters out unsupported elements from an array
 *
 * @param elements an array of elements
 * @param supported an array of supported element types
 */
const removeUnknownElements = (
	elements: ApiResponse.FormElements,
	supported: string[] = Object.keys(componentMap)
) => {
	return elements.filter(e => supported.indexOf(e.elem) !== -1);
};

/**
 * Takes a object(map) of form data and parses the values, returns a new object
 */
export const parseData = (formData: ApiResponse.FormData) => {
	let dirty = false;
	const data: { [k: string]: Array<{ [k: string]: ElementData }> } = {};

	// Loop through each entity we find in the data object
	for (const eid in formData) {
		data[eid] = [];

		// Dirty flag
		if (formData[eid].dirty === true) dirty = true;

		// Each entity has an elements array
		formData[eid].elements.forEach((row, index) => {
			const rowData: {
				[k: string]: ApiResponse.FormInstanceDataItem & { id: string };
			} = {};
			// Each row in the array contains a map of element values
			for (const eid in row) {
				if (row.hasOwnProperty(eid)) {
					// create a value object for each property
					// const yoaf = { id: eid, ...row[eid] }
					rowData[eid] = { id: eid, ...row[eid] };
				}
			}
			data[eid].push(rowData);
		});
	}
	return { dirty, data };
};

/**
 * Takes the JSON representation of a form instance
 * parses it and returns a new object that can
 * be used to create a Form Record
 *
 * @param {ApiResponse.FormInstance} res the form instance JSON
 * @returns {FormRecordConfig}
 */
function normaliseForm(res: ApiResponse.FormInstance): FormConfig {
	const { data, form, instanceId, formLabel, readOnly, bindings } = res;

	// Normalise
	const normalisedElements = normaliseElements(form, form.id);
	const normalisedData = parseData(data);

	return {
		rootElement: form.uuid,
		data: normalisedData.data || {},
		controls: res["@controls"],
		elements: normalisedElements,
		instanceId,
		formLabel,
		readOnly,
		dirty: normalisedData.dirty,
		bindings,
		formLayout: form.formLayout,
	};
}

export default normaliseForm;
